import { forwardRef, useCallback } from 'react'
import { getLevelName, useAddTeam, useProfile } from '@front/model'
import { useToastError, useQueryDisclosure } from '@hooks'
import { Stack } from '@mantine/core'
import { useForm } from '@mantine/form'
import { isDefined, isNotEmpty, type TeamId, type TeamLevel } from '@terros/common'
import { ButtonAdd, type ButtonAddProps } from 'app/components/buttons'
import { TDrawer } from 'app/components/terros'
import { AddTeamForm } from './AddTeamForm'
import { type AddTeamValues } from './types'

type Props = Omit<ButtonAddProps, 'onClick'> & {
  parentId?: TeamId
  level: TeamLevel
  allowParentSelect?: boolean
}

export const AddTeamButton = forwardRef<HTMLButtonElement, Props>(
  ({ parentId, level, allowParentSelect, ...props }, ref) => {
    const { company } = useProfile()
    const levelName = getLevelName(company, level)

    const [open, { toggle, close }] = useQueryDisclosure(`add${level}`)
    const { add, isAdding, error } = useAddTeam()
    useToastError(error)
    const form = useForm<AddTeamValues>({
      initialValues: {
        name: '',
        parentId,
        level,
      },
      validate: {
        name: (value) => (isNotEmpty(value.trim()) ? null : 'Name is required'),
        parentId: (value) => {
          if (company?.highestTeamLevel === level || isDefined(value)) return null
          return 'Parent is required'
        },
      },
    })

    const handleSave = useCallback(
      async (values: AddTeamValues): Promise<void> => {
        await add({
          name: values.name,
          parentId: values.parentId,
          level: values.level,
        })
        form.reset()
        close()
      },
      [add, close]
    )

    return (
      <>
        <TDrawer isChildDrawer opened={open} size='lg' title={`Add ${levelName}`} onClose={close}>
          <form onSubmit={form.onSubmit(handleSave)}>
            <Stack>
              <AddTeamForm allowParentSelect={allowParentSelect} form={form} levelName={levelName} />
              <ButtonAdd loading={isAdding} type='submit'>
                Add
              </ButtonAdd>
            </Stack>
          </form>
        </TDrawer>
        <ButtonAdd {...props} ref={ref} onClick={toggle} />
      </>
    )
  }
)

AddTeamButton.displayName = 'AddTeamButton'
